<script setup>
import { computed, onMounted, ref, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

const props = defineProps({
    slides: {
        type: Array,
        default: () => []
    },
    // For static fallback when slides is empty
    fallbackBg: {
        type: String,
        default: ''
    },
    fallbackTitle: {
        type: String,
        default: ''
    },
    fallbackSubtitle: {
        type: String,
        default: ''
    },
    fallbackDescription: {
        type: String,
        default: ''
    },
    fallbackButtonText: {
        type: String,
        default: ''
    },
    fallbackButtonLink: {
        type: String,
        default: '#'
    },
    fallbackButtonRouteName: {
        type: String,
        default: ''
    },
    // Breadcrumb for page identification
    breadcrumbLabel: {
        type: String,
        default: ''
    }
})

// Helper to check if link is external
const isExternalLink = computed(() => (link) => {
    if (!link) return false
    return link.startsWith('http://') || link.startsWith('https://') || link.startsWith('#')
})

// Navigate to link
const navigate = (link) => {
    if (isExternalLink.value(link)) {
        window.location.href = link
    } else {
        router.push(link)
    }
}

const heroSlider = ref(null)

// Determine hero type: 'empty', 'single', or 'slider'
const heroType = computed(() => {
    if (!props.slides || props.slides.length === 0) return 'empty'
    if (props.slides.length === 1) return 'single'
    return 'slider'
})

// Get the single slide data when heroType is 'single'
const singleSlide = computed(() => {
    return heroType.value === 'single' ? props.slides[0] : null
})

const initCarousel = () => {
    if (window.$ && window.$.fn.owlCarousel && heroSlider.value) {
        window.$(heroSlider.value).owlCarousel({
            items: 1,
            loop: true,
            nav: true,
            dots: true,
            autoplay: false,
            autoplayTimeout: 5000,
            navText: [
                '<i class="uil-arrow-left"></i>',
                '<i class="uil-arrow-right"></i>'
            ]
        })
    } else {
        console.warn('jQuery / Owl Carousel not loaded')
    }
}

onMounted(() => {
    if (heroType.value === 'slider') {
        nextTick(() => {
            initCarousel()
        })
    }
})

watch(() => props.slides.length, (newLength, oldLength) => {
    if (oldLength === 0 && newLength > 1) {
        // Transition from empty/single to slider
        nextTick(() => {
            initCarousel()
        })
    } else if (newLength > 1 && oldLength !== newLength) {
        // Re-init carousel when slides change
        if (window.$ && window.$.fn.owlCarousel && heroSlider.value) {
            window.$(heroSlider.value).trigger('destroy.owl.carousel')
            window.$(heroSlider.value).html(window.$(heroSlider.value).find('.owl-stage-outer').html())
        }
        setTimeout(initCarousel, 100)
    }
})
</script>

<template>
    <!-- EMPTY: Static fallback with provided props -->
    <section v-if="heroType === 'empty'" class="video-wrapper px-0 mt-0 min-vh-80 d-flex align-items-center" :style="{
        backgroundImage: fallbackBg ? `url(${fallbackBg})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
    }">
        <div class="container">
            <div class="row">
                <!-- Breadcrumb if provided -->
                <nav v-if="breadcrumbLabel" aria-label="breadcrumb">
                    <ol class="breadcrumb">
                        <li class="breadcrumb-item active" aria-current="page">
                            {{ breadcrumbLabel }}
                        </li>
                    </ol>
                </nav>

                <div class="text-white">
                    <h1 v-if="fallbackTitle" class="display-1 fs-54 text-white mb-5">
                        <span class="rotator-zoom">{{ fallbackTitle }}</span>
                    </h1>

                    <p v-if="fallbackDescription" class="lead fs-24 mb-0">
                        {{ fallbackDescription }}
                    </p>

                    <div v-if="fallbackButtonText" class="d-flex justify-content-center justify-content-lg-start mt-5">
                        <component :is="isExternalLink(fallbackButtonLink) ? 'a' : 'router-link'"
                            v-bind="isExternalLink(fallbackButtonLink) ? { href: fallbackButtonLink } : { to: fallbackButtonLink }"
                            class="btn btn-lg btn-secondary rounded-pill me-2" style="color: #820358">
                            {{ fallbackButtonText }}
                        </component>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- SINGLE: Static hero with single slide data -->
    <section v-else-if="heroType === 'single'" class="video-wrapper px-0 mt-0 min-vh-80 d-flex align-items-center"
        :style="{
            backgroundImage: singleSlide.picture ? `url(${singleSlide.picture})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
        }">
        <div class="container">
            <div class="row">
                <!-- Breadcrumb if provided -->
                <nav v-if="singleSlide.subtitle || breadcrumbLabel" aria-label="breadcrumb">
                    <ol class="breadcrumb">
                        <li class="breadcrumb-item active" aria-current="page">
                            {{ singleSlide.subtitle || breadcrumbLabel }}
                        </li>
                    </ol>
                </nav>

                <div class="text-white">
                    <h1 v-if="singleSlide.title" class="display-1 fs-54 text-white mb-5">
                        <span class="rotator-zoom">{{ singleSlide.title }}</span>
                    </h1>

                    <p v-if="singleSlide.description" class="lead fs-24 mb-0">
                        {{ singleSlide.description }}
                    </p>

                    <div v-if="singleSlide.textButton"
                        class="d-flex justify-content-center justify-content-lg-start mt-5">
                        <component :is="isExternalLink(singleSlide.linkButton) ? 'a' : 'router-link'"
                            v-bind="isExternalLink(singleSlide.linkButton) ? { href: singleSlide.linkButton } : { to: singleSlide.linkButton }"
                            class="btn btn-lg btn-secondary rounded-pill me-2" style="color: #820358">
                            {{ singleSlide.textButton }}
                        </component>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- SLIDER: OwlCarousel with multiple slides -->
    <div v-else class="hero-slider-wrapper bg-dark">
        <div ref="heroSlider" class="hero-slider owl-carousel dots-over" data-nav="true" data-dots="true"
            data-autoplay="false">
            <div v-for="slide in slides" :key="slide.id" class="owl-slide d-flex align-items-center" :style="{
                backgroundImage: slide.picture ? `url(${slide.picture})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }">
                <div class="container">
                    <div class="row align-items-center">
                        <!-- Breadcrumb if subtitle exists -->
                        <nav v-if="slide.subtitle" aria-label="breadcrumb" class="w-100 mb-3">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item active" aria-current="page">
                                    {{ slide.subtitle }}
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <div class="row align-items-center">
                        <div class="col-12 text-center text-lg-start">
                            <h2 v-if="slide.title" class="display-1 fs-56 mb-4 text-white animated-caption"
                                data-anim="animate__slideInDown" data-anim-delay="500">
                                {{ slide.title }}
                            </h2>

                            <p v-if="slide.description" class="lead fs-23 lh-sm mb-7 text-white animated-caption"
                                data-anim="animate__slideInRight" data-anim-delay="1000">
                                {{ slide.description }}
                            </p>

                            <div v-if="slide.textButton && slide.linkButton" class="animated-caption"
                                data-anim="animate__slideInUp" data-anim-delay="1500">
                                <a :href="slide.linkButton" class="btn btn-lg btn-outline-white rounded-pill">
                                    {{ slide.textButton }}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Additional styles if needed */
</style>
